import { ItemScoreDiamond } from "@/design-system/ItemScore/ItemScoreDiamond";
import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import type { CheapestCombinationRow } from "@repo/shared/types";

/* The two values stack, so the tile only has to be as wide as the longer of
   them — the row is read across, and horizontal space is what it is short of.
   The tile stands for a rating, not for a particular card: a card silhouette
   at this size only reads as a smudge. */
const TILE_WIDTH = 64;

/*
  EA's own bands: bronze below 65, silver below 75, gold above. Spelled out
  rather than composed, because Tailwind only ships the classes it can see.
*/
const metal = (rating: number) =>
  rating >= 75
    ? {
        fill: "from-card-gold to-card-gold-deep",
        edge: "border-card-gold",
      }
    : rating >= 65
      ? {
          fill: "from-card-silver to-card-silver-deep",
          edge: "border-card-silver",
        }
      : {
          fill: "from-card-bronze to-card-bronze-deep",
          edge: "border-card-bronze",
        };

const formatNumber = (value: number) => value.toLocaleString("en-US");

/**
 * The cheapest market-only way to fill a Streamline challenge from scratch: one
 * tile per rating in the cart, the copies needed on its corner, and what one
 * copy costs and is worth on a plate across its foot. The backend sends
 * per-card values; the apps multiply.
 */
export const CheapestCombinationCards = ({
  rows,
  className,
}: {
  rows: CheapestCombinationRow[];
  className?: string;
}) => {
  const { t } = useTranslation(["sbcs", "common"]);
  const coinAssetUrl = `${import.meta.env.VITE_APP_ASSETS_BASE_URL}/misc/coin-tmp.png`;

  return (
    /* Scrolls rather than wraps: a cart is read left to right, and a wrapped
       second line reads as a second requirement. */
    <div
      className={cn(
        "flex gap-3 overflow-x-auto pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
    >
      {rows.map((row) => {
        const { fill, edge } = metal(row.rating);

        return (
          /* One rating per tile, told apart by the gap alone: the tile is
             already an object on the panel, and a box or a rule around each
             turns a short list into a row of panels. */
          <div key={row.rating} className="shrink-0">
            <div className="relative" style={{ width: TILE_WIDTH }}>
              {/* Clips the plate, which runs edge to edge: the tile's own
                  corners are the only rounding in here. */}
              <div
                className={cn(
                  "overflow-hidden rounded-lg bg-linear-to-br",
                  fill,
                )}
              >
                <div className="flex items-center justify-center py-3.5">
                  <span
                    aria-label={`${t("sbcs:streamline.cheapestCombination.rating")} ${row.rating}`}
                    className="text-xl font-extrabold leading-none tabular-nums text-black/75"
                  >
                    {row.rating}
                  </span>
                </div>

                {/* A plate across the foot of the tile, as a card carries its
                    name: the coin and the diamond are their own colours and
                    would be lost read straight off the metal. The two stack
                    and hang off the right edge, so a five-figure price makes
                    the tile taller rather than wider. */}
                <div className="flex w-full flex-col items-end gap-0.5 bg-black/40 px-1.5 py-1 text-[10px] font-semibold tabular-nums">
                  <span
                    aria-label={t("sbcs:streamline.cheapestCombination.price")}
                    className="flex items-center gap-1"
                  >
                    <img
                      src={coinAssetUrl}
                      alt={t("common:alt.coin")}
                      className="size-2.5"
                    />
                    {formatNumber(row.price)}
                  </span>
                  <span
                    aria-label={t(
                      "sbcs:streamline.cheapestCombination.itemScore",
                    )}
                    className="flex items-center gap-1 text-streamline-points"
                  >
                    <ItemScoreDiamond size={9} />
                    {formatNumber(row.itemScore)}
                  </span>
                </div>
              </div>

              {/* Proud of the tile's corner, ringed in the tile's own metal, so
                  the count belongs to that rating and not to the row. White,
                  not the metal: the count has to be read, and the ring already
                  says which tile it belongs to. */}
              <span
                className={cn(
                  "absolute -right-1 -top-1 flex size-6 items-center justify-center rounded-full border bg-surface-raised text-[10px] font-extrabold tabular-nums text-white",
                  edge,
                )}
              >
                {t("sbcs:streamline.stackCount", { total: row.count })}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};
